import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { AlertCircle, CheckCircle2, Inbox } from "lucide-react";
import styles from "./Feedback.module.css";

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>{icon ?? <Inbox aria-hidden />}</div>
      <p className={styles.emptyTitle}>{title}</p>
      {children && <p className={styles.emptyText}>{children}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  return (
    <div className={styles.errorBox} role="alert">
      <AlertCircle aria-hidden />
      <span>{error instanceof Error ? error.message : "Gagal memuat data."}</span>
    </div>
  );
}

export function Skeleton({ height = 16, width = "100%", radius = 6 }: { height?: number; width?: number | string; radius?: number }) {
  return <span className={styles.skeleton} style={{ height, width, borderRadius: radius }} aria-hidden />;
}

/** Loading placeholder shaped like the page it replaces, so nothing jumps when data lands. */
export function PageSkeleton() {
  return (
    <div className={styles.page} aria-busy="true" aria-label="Memuat">
      <Skeleton height={28} width={260} />
      <div className={styles.tiles}>
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} height={104} radius={16} />
        ))}
      </div>
      <Skeleton height={300} radius={16} />
    </div>
  );
}

// ---- toasts -------------------------------------------------------------------

interface Toast {
  id: number;
  tone: "good" | "critical";
  text: string;
}

const ToastContext = createContext<(text: string, tone?: Toast["tone"]) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast["tone"] = "good") => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current.slice(-3), { id, tone, text }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4200);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className={styles.toasts} role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`${styles.toast} ${styles[toast.tone]}`}>
            {toast.tone === "good" ? <CheckCircle2 aria-hidden /> : <AlertCircle aria-hidden />}
            {toast.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
